import { Injectable } from '@angular/core';
import { distinctUntilChanged } from 'rxjs/operators';
import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app';
import type { Auth, User as FirebaseUser } from 'firebase/auth';
import { environment } from '@env/environment';
import { UserApi } from '@shared-features/services/user-api.service';
import { UserType } from '@shared-features/enums';
import { ShipmentCreationTemplateTypes } from '@shared-features/enums/shipments.enum';
import { BidChatApiService } from '@features/bid-chat/services/bid-chat-api.service';
import { AuthSdk, isAuthSdkLoaded, loadAuthSdk } from '@features/bid-chat/services/bid-chat-sdk';

/**
 * Firebase errors that a fresh ID token (stale claims) can fix. The rules also answer this for a write
 * that simply isn't allowed (e.g. a send to a chat that just closed), so it is tried once per session.
 */
const TOKEN_REFRESH_CODES = ['permission-denied'];
/** Errors that mean the session itself is gone: sign in again with a newly minted custom token. */
const SESSION_LOST_CODES = ['unauthenticated', 'auth/user-token-expired', 'auth/invalid-user-token', 'auth/user-disabled'];

const hasBiddingTemplate = (user: { creationTemplates?: { type?: string }[] }): boolean =>
  (user.creationTemplates || []).some((template) => template.type === ShipmentCreationTemplateTypes.Bidding);

const errorCode = (error: unknown): string => (error as { code?: string })?.code ?? '';

/**
 * The Firebase session behind every bid-chat read and write (#47189).
 *
 * - Sign-in (chat contract §3.1): after every "user is logged in" moment — fresh login and session
 *   restore on page load — keep the Firebase session if it belongs to this user, otherwise get a
 *   custom token from `GET chat/firebase-token` and exchange it. Never re-minted on a timer:
 *   `expiresInSeconds` is only the window to exchange it.
 * - Refresh: the session persists across reloads and Firebase renews its ID token (1h) by itself;
 *   every Firestore request carries the current one automatically. `withValidSession()` covers
 *   the two ways it can still fail: stale claims (force-refresh the ID token once per session, retry once) and a lost
 *   session (mint a new custom token, sign in, retry once) — contract §3.3.
 * - Logout: the session ends with the Madar one, so the next account on this browser never reads
 *   the previous one's chats (contract §3.2).
 */
@Injectable({ providedIn: 'root' })
export class BidChatAuthService {
  private signInInFlight: Promise<FirebaseUser> | null = null;
  /** Bumped on every Madar user change, so a sign-in still running for the previous user is dropped. */
  private generation = 0;
  /** The one forced ID-token refresh of this session is spent: later `permission-denied`s are real. */
  private claimsRefreshed = false;

  constructor(private userApi: UserApi, private bidChatApi: BidChatApiService) {
    this.userApi.currentUser$
      .pipe(distinctUntilChanged((a, b) => (a?.id ?? null) === (b?.id ?? null)))
      .subscribe((user) => {
        // Any user change (logout, login, account switch) invalidates what was cached for the last one.
        this.generation++;
        this.signInInFlight = null;
        this.claimsRefreshed = false;
        if (!user?.id) {
          this.signOut();
          return;
        }
        // Signed in up front only for whoever sees the chat icon: a customer with the Bidding template
        // (same rule as the header). Anyone else who opens a chat anyway (an old bidding shipment)
        // signs in on the first Firestore call; fleet and organization users never do. A failure is
        // not shown (the chat just won't load live data) and is retried on the first chat call.
        if (user.roles?.[0]?.userType === UserType.CUSTOMER && hasBiddingTemplate(user)) {
          this.ensureSignedIn().catch(() => undefined);
        }
      });
  }

  get app(): FirebaseApp {
    // The default app is created by `AngularFireModule.initializeApp` (app.module); reuse it.
    return getApps().length ? getApp() : initializeApp(environment.firebase);
  }

  /** The Auth SDK is loaded on first use (`bid-chat-sdk`), not with the app. */
  private async authSdk(): Promise<{ sdk: AuthSdk; auth: Auth }> {
    const sdk = await loadAuthSdk();
    return { sdk, auth: sdk.getAuth(this.app) };
  }

  /** Signs in unless this Madar user already is; concurrent callers share one sign-in. */
  ensureSignedIn(): Promise<FirebaseUser> {
    if (!this.signInInFlight) {
      this.signInInFlight = this.signIn().catch((error) => {
        this.signInInFlight = null;
        throw error;
      });
    }
    return this.signInInFlight;
  }

  /** A valid ID token for this user; Firebase renews it when it is about to expire, or now if `forceRefresh`. */
  private async getToken(forceRefresh = false): Promise<string> {
    const user = await this.ensureSignedIn();
    const { sdk } = await this.authSdk();
    return sdk.getIdToken(user, forceRefresh);
  }

  /**
   * Runs a Firestore call inside a valid session and recovers once from an expired or stale token.
   * Anything else — or a second failure — is rethrown to the caller.
   */
  async withValidSession<T>(call: () => Promise<T>): Promise<T> {
    await this.ensureSignedIn();
    try {
      return await call();
    } catch (error) {
      if (!(await this.recover(error))) throw error;
      return call();
    }
  }

  /** Same recovery for a listener: `true` = the session was repaired, subscribe again. */
  async recover(error: unknown): Promise<boolean> {
    const code = errorCode(error);
    if (TOKEN_REFRESH_CODES.includes(code)) {
      if (this.claimsRefreshed) return false;
      this.claimsRefreshed = true;
      await this.getToken(true);
      return true;
    }
    if (SESSION_LOST_CODES.includes(code)) {
      await this.signOut();
      await this.ensureSignedIn();
      return true;
    }
    return false;
  }

  signOut(): Promise<void> {
    this.signInInFlight = null;
    this.claimsRefreshed = false;
    // Never loads the SDK just to sign out (the login page, fleet users). Customers with the Bidding
    // template load it at session restore, so their persisted session is always signed out here; any
    // other leftover session is replaced at the next sign-in, which checks the uid.
    if (!isAuthSdkLoaded()) return Promise.resolve();
    return this.authSdk()
      .then(({ sdk, auth }) => sdk.signOut(auth))
      .catch(() => undefined);
  }

  private async signIn(): Promise<FirebaseUser> {
    const user = this.userApi.currentUser$.value;
    if (!user?.id) throw new Error('Not logged in');
    const generation = this.generation;
    const stillCurrent = (): void => {
      if (generation !== this.generation) throw new Error('The Madar user changed during sign-in');
    };

    const { sdk, auth } = await this.authSdk();
    // A persisted session for this user needs no token at all (page reloads).
    const restored = await this.restoredUser(sdk, auth);
    stillCurrent();
    if (restored?.uid === String(user.id)) return restored;
    if (restored) await sdk.signOut(auth);

    const { firebaseToken } = await this.bidChatApi.getFirebaseToken().toPromise();
    stillCurrent();
    if (!firebaseToken) throw new Error('No Firebase token returned');
    const signedIn = (await sdk.signInWithCustomToken(auth, firebaseToken)).user;
    if (generation !== this.generation) {
      // Finished after a logout or account switch: never leave the previous user signed in.
      await sdk.signOut(auth).catch(() => undefined);
      throw new Error('The Madar user changed during sign-in');
    }
    return signedIn;
  }

  /** The persisted session is restored asynchronously; `auth.currentUser` is null until then. */
  private restoredUser(sdk: AuthSdk, auth: Auth): Promise<FirebaseUser | null> {
    return new Promise((resolve) => {
      const unsubscribe = sdk.onAuthStateChanged(auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }
}
